import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Target, Users, Lightbulb, Heart, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent } from '../data/mock';

export default function AboutPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-teal-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm tracking-wide uppercase mb-4">
              <Lightbulb className="w-4 h-4" />
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Transforming Lives, Strengthening Communities</h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              The Productive Offenders of Society Foundation is dedicated to breaking the cycle of recidivism through comprehensive support, education, and employment opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-amber-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">Our Story</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-8">{aboutContent.story.title}</h2>
              <div className="space-y-6">
                {aboutContent.story.paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-slate-600 leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_posfoundation/artifacts/t4bqheps_IMG_1669.jpeg"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">Est.</div>
                <div className="text-2xl font-semibold text-teal-200">2023</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-teal-50/50 via-slate-50 to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">A Message From Our Founder</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Words of Resilience & Hope</h2>
            </div>
            
            <Card className="border-0 shadow-xl bg-white overflow-hidden">
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold">JT</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{aboutContent.founder.name}</h3>
                    <p className="text-teal-200">{aboutContent.founder.title}, Productive Offenders of Society Foundation</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-8 lg:p-12">
                <Quote className="w-12 h-12 text-teal-100 mb-6" />
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  {aboutContent.founder.message.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <p className="text-slate-500 italic">{aboutContent.founder.closing},</p>
                  <p className="font-semibold text-teal-600 mt-1">{aboutContent.founder.name}</p>
                </div>
              </CardContent>
            </Card>
            
            <div className="mt-8 text-center">
              <p className="text-slate-500 text-sm italic">
                "We stand with JT. We stand for reentry. We stand for second chances."
              </p>
              <p className="text-teal-600 font-medium mt-2">— Productive Offenders of Society Foundation Team</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 lg:py-28 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-teal-400 font-semibold text-sm tracking-wide uppercase">The Challenge</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-6">{aboutContent.problem.title}</h2>
            <p className="text-slate-300 text-lg">{aboutContent.problem.description}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {aboutContent.problem.stats.map((stat, index) => (
              <div key={index} className="text-center p-8 bg-slate-800/50 rounded-2xl border border-slate-700">
                <div className="text-4xl sm:text-5xl font-bold text-teal-400 mb-3">{stat.number}</div>
                <p className="text-slate-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Create Change */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-amber-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">Our Approach</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">{aboutContent.approach.title}</h2>
            <p className="text-slate-600">A proven, step-by-step approach to help individuals achieve lasting success.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.approach.steps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="border-0 shadow-sm hover:shadow-lg transition-shadow h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-teal-600">{index + 1}</span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg mb-2">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.description}</p>
                  </CardContent>
                </Card>
                {index < aboutContent.approach.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="w-8 h-8 text-slate-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">What Drives Us</h2>
            <p className="text-slate-600">The principles that guide our work and inspire our mission.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Target className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-xl mb-3">Empowerment</h3>
                <p className="text-slate-600">We believe in the potential of every individual to transform their lives and achieve their goals.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Lightbulb className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-xl mb-3">Education</h3>
                <p className="text-slate-600">Knowledge and skills development are the foundation of lasting positive change and success.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-xl mb-3">Community</h3>
                <p className="text-slate-600">Together, we create stronger, safer communities where everyone has the opportunity to thrive.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Join Our Mission</h2>
          <p className="text-xl text-teal-100 mb-10">
            Together, we can break the cycle and build a brighter future for everyone.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
              <Link to="/get-involved">
                Get Involved
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
